import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanel, SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setPdpaConsent } from '@/stores/appSlice';
import { selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PdpaNoticeBody } from './pdpaNoticeContent';

export default function Pdpa() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const [consentWarning, setConsentWarning] = useState<string | null>(null);

  useEffect(() => {
    if (pdpaConsent) setConsentWarning(null);
  }, [pdpaConsent]);

  const handleAgree = () => {
    if (!pdpaConsent) {
      setConsentWarning(t.pdpa.consentWarning);
      return;
    }
    navigate('/phone');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col pt-[10%] pb-8">
        <SurveyContentPanelInset className="mt-4 flex min-h-0 flex-1 flex-col">
          <SurveyContentPanel className="flex min-h-0 flex-1 flex-col overflow-hidden !p-0 max-h-[70dvh]">
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
              <h1 className="text-center text-[36px] font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[42px]">
                {t.pdpa.title}
              </h1>
              <h2 className="mt-4 text-center text-[14px] font-bold leading-snug tracking-wide text-white/95 sm:text-[15px]">
                {t.pdpa.noticeTitle}
              </h2>
              <PdpaNoticeBody />
            </div>
          </SurveyContentPanel>
        </SurveyContentPanelInset>

        <SurveyContentPanelInset className="mt-4 shrink-0">
          <SurveyQuestionPanel>
            <label
              htmlFor="pdpa-consent"
              className="flex cursor-pointer items-start gap-3 text-justify text-[13px] font-bold leading-snug text-white/95 sm:text-[14px]"
            >
            <input
              id="pdpa-consent"
              type="checkbox"
              checked={pdpaConsent}
              onChange={(e) => dispatch(setPdpaConsent(e.target.checked))}
              className={`${surveyChoiceInputClass} rounded border-2 border-white/50 bg-black/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80`}
            />
            <span>{t.pdpa.consentText}</span>
          </label>
          </SurveyQuestionPanel>
        </SurveyContentPanelInset>

        <SurveyContentPanelInset className="mt-6 shrink-0">
          <div className="flex flex-col items-center gap-3">
            {consentWarning != null ? (
              <SurveyValidationAlert>{consentWarning}</SurveyValidationAlert>
            ) : null}
            <div className="flex w-full flex-nowrap items-center justify-evenly gap-3">
              <SurveyButton onClick={handleAgree}>{t.pdpa.agree}</SurveyButton>
              <SurveyButton onClick={() => navigate('/thankyou')}>{t.pdpa.decline}</SurveyButton>
            </div>
          </div>
        </SurveyContentPanelInset>
      </div>
    </SurveyBackgroundLayout>
  );
}
